import { useEffect, useState } from "react";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSChip from "views/system-design/DSChip";
import DSTextField from "views/system-design/DSTextField";
import DSSwitch from "views/system-design/DSSwitch";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import firebaseService from "../../../firebase_service/firebaseService";
import { FeatureBetaGroup } from "../../../api/admin";
import {
  useCreateFeatureBetaGroup,
  useUpdateFeatureBetaGroup,
} from "../hooks/useFeatureBetaGroups";

interface FeatureBetaGroupModalProps {
  open: boolean;
  onClose: () => void;
  // Null creates a new group; a group edits it in place.
  group?: FeatureBetaGroup | null;
}

const FeatureBetaGroupModal = ({
  open,
  onClose,
  group,
}: FeatureBetaGroupModalProps) => {
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [description, setDescription] = useState("");
  const [hardVersion, setHardVersion] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already cached client-side by the firebase service, so no extra fetch.
  const general = firebaseService.general;

  const { mutate: create, isPending: isCreating } = useCreateFeatureBetaGroup();
  const { mutate: update, isPending: isUpdating } = useUpdateFeatureBetaGroup();

  useEffect(() => {
    if (open) {
      setName(group?.name ?? "");
      setVersion(group ? String(group.version) : "");
      setDescription(group?.description ?? "");
      setHardVersion(group?.hardVersion ?? false);
      setError(null);
    }
  }, [open, group]);

  const trimmedName = name.trim();
  const versionNumber = Number(version);
  const isPending = isCreating || isUpdating;
  const canSave =
    !isPending &&
    trimmedName.length > 0 &&
    Number.isFinite(versionNumber) &&
    versionNumber > 0;

  // The API rejects a version with no APK in the updates bucket, which is the
  // most likely failure here, so its message is surfaced verbatim.
  const handleError = (e: unknown) => {
    const message =
      (e as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? (e instanceof Error ? e.message : "Something went wrong");
    setError(message);
  };

  const handleSave = () => {
    setError(null);
    const data = {
      name: trimmedName,
      version: versionNumber,
      description: description.trim() || null,
      hardVersion,
    };
    if (group) {
      update(
        { id: group.id, data },
        { onSuccess: onClose, onError: handleError },
      );
    } else {
      create(data, { onSuccess: onClose, onError: handleError });
    }
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>
        {group ? `Edit ${group.name}` : "New feature beta group"}
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <DSTextField
            label="Name"
            size="small"
            value={name}
            placeholder="e.g. pendulum-v2"
            onChange={(e) => setName(e.target.value)}
          />

          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTextField
              label="Version"
              type="number"
              size="small"
              value={version}
              placeholder="e.g. 512"
              onChange={(e) => setVersion(e.target.value)}
            />
            <DSTypography variant="caption" color="text.secondary">
              {hardVersion
                ? "Clocks in this group install exactly this version, even if it is older than their normal target."
                : "Clocks in this group update to max(their normal target, this version)."}{" "}
              The version must already have an APK in the updates bucket.
            </DSTypography>
            {/* The published versions, so picking one does not mean a trip to
                the Versions page. Click to fill the field. */}
            {general && (
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <DSTypography variant="caption" color="text.secondary">
                  Latest:
                </DSTypography>
                {[
                  { label: "last", value: general.last },
                  { label: "beta", value: general.beta },
                  { label: "public", value: general.public },
                ]
                  .filter(({ value }) => value != null)
                  .map(({ label, value }) => (
                    <DSChip
                      key={label}
                      size="small"
                      label={`${label} ${value}`}
                      variant={
                        String(value) === version.trim() ? "filled" : "outlined"
                      }
                      onClick={() => setVersion(String(value))}
                    />
                  ))}
              </DSBox>
            )}
          </DSBox>

          <DSBox sx={{ display: "flex", flexDirection: "column" }}>
            <DSFormControlLabel
              control={
                <DSSwitch
                  size="small"
                  checked={hardVersion}
                  onChange={(e) => setHardVersion(e.target.checked)}
                />
              }
              label="Hard version"
            />
            <DSTypography variant="caption" color="text.secondary">
              A clock&apos;s own hard version still wins. A clock in two active
              hard version groups ignores both pins and gets the highest group
              version as a minimum.
            </DSTypography>
          </DSBox>

          <DSTextField
            label="Description (optional)"
            size="small"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {error && (
            <DSTypography variant="caption" color="error">
              {error}
            </DSTypography>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <DSButton size="small" onClick={onClose}>
          Cancel
        </DSButton>
        <DSButton
          variant="contained"
          size="small"
          disabled={!canSave}
          onClick={handleSave}
        >
          {group ? "Save" : "Create"}
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default FeatureBetaGroupModal;
